import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import useAutomations from '../../../hooks/automationhooks/useAutomations';
import FormTemplate from '../../../forms/FormTemplate';
import {
    Autocomplete,
    Button,
    Grid,
    TextField,
    Typography
} from '@mui/material';
import { useTheme } from '@emotion/react';

export default function CRMSideMenu({ onClose, onSubmit, selectedNode, preSelectedTemplate = null }) {
    const { brandId } = useParams();
    const { handleGetCampaigns, handleGetIntegrations, campaigns } = useAutomations();
    const [selectedCampaign, setSelectedCampaign] = useState();
    const [selectedIntegration, setSelectedIntegration] = useState();
    const [integrations, setIntegrations] = useState([]);
    const [loading, setLoading] = useState(false);
    const theme = useTheme();
    const primaryColor = theme.palette.background.default;

    useEffect(() => {
        if (brandId) {
            handleGetCampaigns(brandId);
        }
    }, [brandId]);

    const fetchIntegrations = useCallback(async () => {
        setLoading(true);
        try {
            const response = await handleGetIntegrations(brandId, selectedCampaign.Id);
            setIntegrations(response.data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [brandId, selectedCampaign]);

    const handleCampaignChange = (event, newValue) => {
        setSelectedCampaign(newValue);
        setSelectedIntegration(null); // reset selection when campaign changes
        setIntegrations([]);
    };

    const handleSubmitLocal = () => {
        const data = {
            id: selectedIntegration.Id,
            name: selectedIntegration.Name
        };
        onSubmit(data);
        onClose();
    };

    return (
        <FormTemplate
            title={selectedNode ? 'Edit CRM Node' : 'Create CRM Node'}
            isValid={selectedIntegration}
            onClose={onClose}
            handleSubmit={handleSubmitLocal}
        >
            <Grid container direction="column" spacing={3}>
                <Grid item>
                    <Autocomplete
                        options={campaigns.data || []}
                        sx={{ width: 300 }}
                        value={selectedCampaign}
                        onChange={handleCampaignChange}
                        getOptionLabel={(option) => option?.Name || ''}
                        isOptionEqualToValue={(option, value) => option?.Id === value?.Id}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Select Campaign"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: primaryColor,
                                    },
                                }}
                            />
                        )}
                    />
                </Grid>


                <Grid item>
                    {loading ? (
                        <Typography>Loading integrations...</Typography>
                    ) : integrations.length === 0 ? (
                        <Typography>No Integrations Found</Typography>
                    ) : (
                        <Autocomplete
                            options={integrations}
                            value={selectedIntegration}
                            onChange={(event, value) => {
                                setSelectedIntegration(value);
                            }}
                            getOptionLabel={(option) => option.Name || option.Id?.toString() || 'Unnamed'}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Select Integration"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: primaryColor,
                                        },
                                    }}
                                />
                            )}
                            sx={{ width: 300 }}
                        />
                    )}
                </Grid>
                <Grid item>
                    <Button
                        onClick={fetchIntegrations}
                        disabled={!selectedCampaign}
                        variant="contained"
                    >
                        Load Integrations
                    </Button>
                </Grid>
            </Grid>
        </FormTemplate>
    );
}
